import { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { MONO_FONT, RADIUS } from '../../../../components/ui/tokens';
import { Log, MasterOrderInfo, SlaveOrder, TpSlConfig } from '../../../../proto/trade-copier_pb';
import { SoftAlert } from '../../../mcp/mcpUi';
import { Eyebrow, ink } from '../../../mcp/SignalDesk';
import { riseItem, staggerParent } from '../../../rooms/rooms.motion';
import { useTourMotion } from '../../../welcome-tour/motion';
import { useSimulateMeta, useSimulateResult, useSimulateState } from '../SimulateTradeContext';
import { LOG_SEVERITY, splitOrderType } from '../constants';
import { SIMULATE_COPY } from '../content';
import { CalloutTone, SimulateCallout } from './SimulateCallout';
import { formatExpiration, formatNumber, formatOptional, formatOrderType, formatRatio, formatText, formatTime } from '../format';

const { result } = SIMULATE_COPY;
const { rows: ROW, changes: CHANGE } = result;

type Tone = CalloutTone;
const TONE_ICONS: Record<Tone, ReactNode> = {
  primary: <InfoCircleOutlined />,
  success: <CheckCircleOutlined />,
  warning: <ExclamationCircleOutlined />,
  error: <CloseCircleOutlined />
};

interface CompareRow {
  key: string;
  label: string;
  master: string;
  slave: string;
  change?: string;
  /** Changes that lose something (e.g. SL not copied) read as a warning. */
  lossy?: boolean;
}

const severityOf = (log: Log.AsObject) => LOG_SEVERITY[log.level.toLowerCase()] ?? 'info';
const messageOf = (log: Log.AsObject) => log.error?.value || log.type;

/** SL / TP: dropped, added (fixed SL/TP on the slave) or moved. */
const levelChange = (master: number, slave: number): Pick<CompareRow, 'change' | 'lossy'> => {
  if (master && !slave) return { change: CHANGE.notCopied, lossy: true };
  if (!master && slave) return { change: CHANGE.added };
  if (master !== slave) return { change: CHANGE.changed };
  return {};
};

const buildRows = (master: MasterOrderInfo.AsObject, slave: SlaveOrder.AsObject): CompareRow[] => {
  const masterSide = splitOrderType(master.orderType).side;
  const slaveSide = splitOrderType(slave.type).side;
  const rows: CompareRow[] = [
    {
      key: 'symbol',
      label: ROW.symbol,
      master: formatText(master.symbol),
      slave: formatText(slave.symbol),
      change: slave.symbol && slave.symbol !== master.symbol ? CHANGE.mapped : undefined
    },
    {
      key: 'order',
      label: ROW.order,
      master: formatOrderType(master.orderType),
      slave: formatOrderType(slave.type),
      change: masterSide !== slaveSide ? CHANGE.reversed : slave.type !== master.orderType ? CHANGE.changed : undefined
    },
    {
      key: 'lots',
      label: ROW.lots,
      master: formatNumber(master.lots),
      slave: formatNumber(slave.lots),
      change: master.lots > 0 && slave.lots !== master.lots ? CHANGE.ratio(formatRatio(slave.lots, master.lots)) : undefined
    },
    {
      key: 'openPrice',
      label: ROW.openPrice,
      master: formatOptional(master.openPrice),
      slave: formatOptional(slave.openPrice),
      change: master.openPrice && slave.openPrice && master.openPrice !== slave.openPrice ? CHANGE.changed : undefined
    },
    {
      key: 'stopLoss',
      label: ROW.stopLoss,
      master: formatOptional(master.stopLoss),
      slave: formatOptional(slave.stopLoss),
      ...levelChange(master.stopLoss, slave.stopLoss)
    },
    {
      key: 'takeProfit',
      label: ROW.takeProfit,
      master: formatOptional(master.takeProfit),
      slave: formatOptional(slave.takeProfit),
      ...levelChange(master.takeProfit, slave.takeProfit)
    },
    {
      key: 'magic',
      label: ROW.magic,
      master: formatNumber(master.magicNumber),
      slave: formatNumber(slave.magicNumber),
      change: master.magicNumber !== slave.magicNumber ? CHANGE.changed : undefined
    },
    {
      key: 'comment',
      label: ROW.comment,
      master: formatText(master.comment),
      slave: formatText(slave.comment),
      change: master.comment.trim() !== slave.comment.trim() ? CHANGE.changed : undefined
    }
  ];

  if (master.placedType || slave.placedType) {
    rows.push({
      key: 'placedType',
      label: ROW.placedType,
      master: formatText(master.placedType),
      slave: formatText(slave.placedType),
      change: master.placedType !== slave.placedType ? CHANGE.changed : undefined
    });
  }
  if (master.expiration || slave.expiration) {
    rows.push({
      key: 'expiration',
      label: ROW.expiration,
      master: formatExpiration(master.expiration),
      slave: formatExpiration(slave.expiration),
      change: master.expiration !== slave.expiration ? CHANGE.changed : undefined
    });
  }
  if (slave.delayuntil?.seconds) {
    rows.push({
      key: 'delay',
      label: ROW.delay,
      master: SIMULATE_COPY.empty,
      slave: formatTime(slave.delayuntil.seconds),
      change: CHANGE.added
    });
  }
  return rows;
};

/** Why nothing would be copied: the copier's first log, or the one silent skip the request itself explains. */
const skippedVerdict = (logs: Log.AsObject[], masterOrder: MasterOrderInfo.AsObject, copiesPending: boolean) => {
  if (logs[0]) {
    return { tone: severityOf(logs[0]) === 'error' ? 'error' : 'warning', title: result.skipped, caption: messageOf(logs[0]) } as const;
  }
  // trade-manager skips a filled pending order with no log when the slave copies pending orders (processSlaveConfigForSend).
  if (masterOrder.isPendingFill && copiesPending) {
    return { tone: 'primary', title: result.pendingHeld.title, caption: result.pendingHeld.caption } as const;
  }
  return { tone: 'warning', title: result.skipped, caption: result.noOrder } as const;
};

const Verdict = ({ tone, title, caption, badge }: { tone: Tone; title: string; caption: string; badge?: string }) => {
  const theme = useTheme();
  return (
    <SimulateCallout
      tone={tone}
      icon={TONE_ICONS[tone]}
      title={title}
      caption={caption}
      aside={
        badge && (
          <Typography
            variant="caption"
            sx={{
              px: 1.25,
              py: 0.5,
              fontWeight: 600,
              borderRadius: RADIUS.pill,
              color: ink(theme),
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            {badge}
          </Typography>
        )
      }
    />
  );
};

const valueSx = { fontFamily: MONO_FONT, fontSize: 13, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all' } as const;

// Cells stretch to the row height and centre their content, so every row's divider sits on one line;
// spacing is right padding, not a column gap, so the dividers run unbroken across the table.
const ROW_MIN_HEIGHT_PX = 40;

const CompareTable = ({ rows }: { rows: CompareRow[] }) => {
  const theme = useTheme();
  const cell = {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    minHeight: ROW_MIN_HEIGHT_PX,
    py: 1,
    pr: 1.5,
    borderBottom: `1px solid ${theme.palette.divider}`
  } as const;
  const headerCell = { ...cell, minHeight: 'auto', pt: 0 } as const;
  return (
    <Box
      role="table"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '84px minmax(0, 1fr) 28px minmax(0, 1fr)', sm: '120px minmax(0, 1fr) 36px minmax(0, 1.4fr)' }
      }}
    >
      <Box role="row" sx={{ display: 'contents' }}>
        <Box role="columnheader" sx={headerCell} />
        <Box role="columnheader" sx={headerCell}>
          <Eyebrow>{result.master}</Eyebrow>
        </Box>
        <Box role="columnheader" sx={headerCell} />
        <Box role="columnheader" sx={headerCell}>
          <Eyebrow>{result.slave}</Eyebrow>
        </Box>
      </Box>
      {rows.map((row) => {
        const changeColor = row.lossy ? ink(theme, 'warning') : ink(theme);
        return (
          <Box key={row.key} role="row" sx={{ display: 'contents' }}>
            <Typography role="rowheader" variant="body2" color="text.secondary" sx={cell}>
              {row.label}
            </Typography>
            <Typography role="cell" sx={{ ...cell, ...valueSx }}>
              {row.master}
            </Typography>
            <Box aria-hidden sx={{ ...cell, justifyContent: 'center', fontSize: 11, color: row.change ? changeColor : 'text.disabled' }}>
              <ArrowRightOutlined />
            </Box>
            <Box role="cell" sx={{ ...cell, flexWrap: 'wrap', gap: 1, rowGap: 0.5 }}>
              <Typography sx={{ ...valueSx, fontWeight: row.change ? 700 : 400, color: row.change ? changeColor : 'text.primary' }}>
                {row.slave}
              </Typography>
              {row.change && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    px: 0.9,
                    py: 0.1,
                    fontWeight: 600,
                    borderRadius: RADIUS.pill,
                    color: changeColor,
                    bgcolor: alpha(changeColor, 0.12)
                  }}
                >
                  {row.change}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/** Broker-side SL/TP the slave order carries (set values only). */
const tpSlItems = (config?: TpSlConfig.AsObject): string[] =>
  (Object.keys(result.tpSl) as (keyof TpSlConfig.AsObject)[])
    .filter((key) => config?.[key] !== undefined)
    .map((key) => `${result.tpSl[key]} ${formatNumber(config?.[key]?.value ?? 0)}`);

const BrokerTpSl = ({ items }: { items: string[] }) => (
  <Stack spacing={1}>
    <Eyebrow>{result.brokerTpSl}</Eyebrow>
    <Typography sx={{ ...valueSx, color: 'text.secondary' }}>{items.join('  ·  ')}</Typography>
  </Stack>
);

const Reasons = ({ logs }: { logs: Log.AsObject[] }) => (
  <Stack spacing={1}>
    <Eyebrow>{result.why}</Eyebrow>
    {logs.map((log, index) => (
      <SoftAlert key={log.id || index} severity={severityOf(log)}>
        {messageOf(log)}
      </SoftAlert>
    ))}
  </Stack>
);

/** The verdict, what the slave would place against what the master sent, and the copier's reasons. */
export const SimulateResultPanel = () => {
  const { v } = useTourMotion();
  const { data, logs } = useSimulateResult();
  const { masterOrder } = useSimulateState();
  const { getSlaveConfigDraft } = useSimulateMeta();

  // Skipped trades lead with the first reason, so the list carries the rest.
  const reasons = data ? logs : logs.slice(1);
  const tpSl = tpSlItems(data?.tpSlConfigs);
  const sections: { key: string; node: ReactNode }[] = [
    {
      key: 'verdict',
      node: data ? (
        <Verdict
          tone="success"
          title={result.copied}
          caption={result.notes(logs.length)}
          badge={data.fromPendingOrder ? result.fromPending : undefined}
        />
      ) : (
        <Verdict {...skippedVerdict(logs, masterOrder, Boolean(getSlaveConfigDraft().copyPendingOrders?.value))} />
      )
    }
  ];
  if (data) sections.push({ key: 'compare', node: <CompareTable rows={buildRows(masterOrder, data)} /> });
  if (tpSl.length) sections.push({ key: 'tpsl', node: <BrokerTpSl items={tpSl} /> });
  if (reasons.length) sections.push({ key: 'why', node: <Reasons logs={reasons} /> });

  return (
    <Stack component={motion.div} variants={v(staggerParent())} initial="hidden" animate="show" spacing={2.5}>
      {sections.map((section) => (
        <Box key={section.key} component={motion.div} variants={v(riseItem)}>
          {section.node}
        </Box>
      ))}
    </Stack>
  );
};
